/**
 * My Chapter — First Chapter support page
 * Route: /demo/cycle/mom · /demo/first-chapter/mom
 * PageHeader + status + Nova strip are rendered by FertilityDemoSection.
 */

import { useRef, useState, useCallback } from 'react';
import { useApp } from '../../context/AppContext';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { tabNavContainer, tabNavItemColored } from '../../designSystem';
import NovaAvatarImg from '../../components/NovaAvatarImg';

const FC = {
  accent: '#7F77DD',
  accentDark: '#534AB7',
  accentDeep: '#3C3489',
  accentSoft: '#EEEDFE',
  accentBorder: 'rgba(127, 119, 221, 0.42)',
  gradStart: '#EEEDFE',
  gradEnd: '#E1F5EE',
  pageBg: '#FFFCF9',
  border: '#E8E0D8',
  ink: '#111111',
  body: '#555555',
  muted: '#888888',
};

const FONT_SERIF = "'Playfair Display', Georgia, serif";
const FONT_SANS = "'Plus Jakarta Sans', -apple-system, sans-serif";

function SecLabel({ children }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
      <span
        style={{
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: '0.12em',
          color: FC.muted,
          textTransform: 'uppercase',
          fontFamily: FONT_SANS,
          whiteSpace: 'nowrap',
        }}
      >
        {children}
      </span>
      <div style={{ flex: 1, height: 0.5, background: FC.border }} />
    </div>
  );
}

function BodySegments({ parts, color = FC.body }) {
  return (
    <p style={{ fontSize: 12, color, lineHeight: 1.65, margin: 0, fontFamily: FONT_SANS }}>
      {parts.map((p, i) =>
        p.bold ? (
          <strong key={i} style={{ fontWeight: 700, color: FC.ink }}>
            {p.text}
          </strong>
        ) : (
          <span key={i}>{p.text}</span>
        )
      )}
    </p>
  );
}

const FEATURES = [
  {
    number: '01',
    topic: 'Mood & rage',
    imageSrc: '/images/chapter-heroes/cycle.png',
    imageAlt: 'Illustration for First Chapter — cycle support',
    accentColor: '#7F77DD',
    eyebrowColor: '#534AB7',
    title: "That's your hormones. Not your personality.",
    quote: '"I was completely fine and then I wasn\'t and I still don\'t know what happened."',
    quoteColors: { bg: '#EEEDFE', text: '#3C3489' },
    bodyParts: [
      { text: 'In the 1–2 weeks before your period, a hormonal drop makes your nervous system ' },
      { text: 'genuinely more reactive to stress.', bold: true },
      {
        text:
          " The irritability, tears, or full-volume rage at something small — that's not a character flaw. It's physiological. It follows a pattern every month, which means you can track it, predict it, and stop being blindsided by it.",
      },
    ],
    body2:
      'The first time you can say "I think I\'m just in my difficult week" instead of wondering what\'s wrong with you, something shifts. Naming it is more powerful than it sounds.',
    actions: [
      {
        name: 'Track the pattern',
        desc: 'Note your mood alongside your cycle for 2 months. It becomes predictable — and predictable is less scary.',
      },
      {
        name: 'Name it out loud',
        desc: '"I think I\'m in my difficult week." To yourself, or someone you trust. Naming it genuinely reduces the intensity.',
      },
      {
        name: '4-7-8 breathing',
        desc: 'Breathe in 4 counts, hold 7, out 8. Does something real to your nervous system when the rage peaks.',
      },
    ],
    oraTip:
      'Severe mood changes tied to your cycle have a name: PMDD. Real, common, treatable. If it\'s affecting your relationships or your sense of yourself, it\'s worth mentioning to a doctor.',
    oraNoteColors: { bg: '#EEEDFE', labelColor: '#534AB7', textColor: '#3C3489' },
    footer: { left: 'Want to go deeper?', right: 'Ask Nova about mood and your cycle →', novaPrefill: 'Tell me about mood and my cycle' },
  },
  {
    number: '02',
    topic: 'Energy & sleep',
    imageSrc: '/images/hero-women.jpg',
    imageAlt: 'Women supporting each other',
    accentColor: '#E8602C',
    eyebrowColor: '#C94E22',
    title: "You're not lazy. Your luteal phase is sedating you.",
    quote: '"I slept nine hours and I\'m still exhausted. There must be something wrong with me."',
    quoteColors: { bg: '#FEF0EB', text: '#AD3F1A' },
    bodyParts: [
      {
        text:
          'Progesterone — which peaks in the week or two before your period — acts like a mild sedative on your brain. It also disrupts deep sleep quality. ',
      },
      { text: "You're not imagining the exhaustion.", bold: true },
      { text: " Your body is running heavy background processes you can't see." },
    ],
    body2:
      'Energy typically peaks around ovulation (day 14 is often the highest point). The week before your period is the lowest. Knowing the pattern means you can stop fighting your own biology and start working with it.',
    actions: [
      {
        name: 'Work with your cycle, not against it',
        desc: 'Save demanding tasks for days 6–14. In the luteal phase, lower the bar on yourself. That\'s not laziness — it\'s accuracy.',
      },
      {
        name: 'Cool room for sleep',
        desc: 'Body temperature rises pre-period. A slightly cooler room meaningfully improves sleep quality in the luteal phase.',
      },
      {
        name: 'Magnesium before bed',
        desc: 'Magnesium glycinate specifically. Evidence-backed for both sleep quality and reducing pre-menstrual mood symptoms.',
      },
    ],
    oraTip:
      "You're on day __CYCLE_DAY__ — this tends to be an energy peak for most people. If you're exhausted right now anyway, that's worth noting in your period log.",
    oraNoteColors: { bg: '#FEF0EB', labelColor: '#C94E22', textColor: '#AD3F1A' },
    footer: { left: "Exhausted and can't explain it?", right: 'Ask Nova →', novaPrefill: "I'm exhausted and can't explain it — help" },
  },
  {
    number: '03',
    topic: 'Body image',
    imageSrc: '/images/chapters/cycle.png',
    imageAlt: 'First Chapter — your body, your cycle',
    accentColor: '#D4537E',
    eyebrowColor: '#72243E',
    title: "You're comparing a real body to a curated one. That's not a fair fight.",
    quote: '"I looked fine this morning. Then I scrolled for ten minutes and now I hate everything."',
    quoteColors: { bg: '#FBEAF0', text: '#72243E' },
    bodyParts: [
      {
        text:
          'Your body changes visibly throughout your cycle — bloating, skin changes, slight weight shifts. Social media shows bodies at their most controlled and edited. ',
      },
      { text: 'Comparing a period body to a filtered photo is not a fair comparison', bold: true },
      { text: " — and it's not an accurate one." },
    ],
    body2:
      'The 5 days before your period are specifically when body image tends to crash hardest. That\'s hormonal too. Knowing the window doesn\'t make it disappear, but it tells you when to be especially careful with what you\'re scrolling through.',
    actions: [
      {
        name: 'Luteal phase scroll limit',
        desc: 'Specifically the 5 days before your period. Set a timer. You\'ll feel the difference — that\'s the window when comparison hits hardest.',
      },
      {
        name: 'What your body does, not how it looks',
        desc: '"My legs got me here. My hands made that thing." Functional appreciation is neurologically different from appearance-based thinking.',
      },
      {
        name: 'Notice the pattern',
        desc: 'Track body image alongside your cycle. When you can see it correlates with day 24-28, it becomes data — not truth.',
      },
    ],
    oraTip:
      "If thoughts about your body are frequent, intrusive, or affecting what you eat — that's worth talking through. You can start with Nova. No judgment, ever.",
    oraNoteColors: { bg: '#FBEAF0', labelColor: '#72243E', textColor: '#993556' },
    footer: { left: 'Want to talk through this?', right: 'Open Nova →', novaPrefill: 'I want to talk about body image and my cycle' },
  },
  {
    number: '04',
    topic: 'Relationships + your body',
    imageSrc: '/images/chapter-heroes/fertility.png',
    imageAlt: 'Support and care',
    accentColor: '#378ADD',
    eyebrowColor: '#185FA5',
    title: 'Your body belongs to you. "I don\'t want to" is a complete sentence.',
    quote: '"Something happened and I can\'t tell if it was okay or not. I keep going back and forth."',
    quoteColors: { bg: '#E6F1FB', text: '#0C447C' },
    bodyParts: [
      {
        text:
          'Body literacy — understanding your own body — connects directly to knowing what belongs to you and what doesn\'t. ',
      },
      { text: 'If something felt wrong, that matters. Even if you can\'t explain why yet.', bold: true },
      { text: " You don't need a reason to say no, or to say stop, or to not want something." },
    ],
    body2:
      'Uncertainty about whether something was okay is itself an answer worth paying attention to. You\'re allowed to take time to figure out how you feel. You\'re allowed to change your mind. You\'re allowed to name it later.',
    actions: [
      {
        name: 'Talk to Nova first',
        desc: 'Nova is a private space to work out how you feel before you have to tell anyone else. No record, no judgment.',
      },
      {
        name: 'Know your "no" shapes',
        desc: '"I don\'t want to." "I want to stop." "That doesn\'t feel right." All complete. None require an explanation or an apology.',
      },
      {
        name: 'You can name it later',
        desc: "You don't have to figure out what happened or what to call it right now. Taking time to process is normal and allowed.",
      },
    ],
    oraTip:
      "If something happened that you're not sure about — Nova is the right first place to bring it. She can also help you figure out if and how you want to tell someone else.",
    oraNoteColors: { bg: '#E6F1FB', labelColor: '#185FA5', textColor: '#0C447C' },
    footer: {
      left: "Something happened and you're not sure about it?",
      right: 'Talk to Nova — privately →',
      novaPrefill: "Something happened and I'm not sure how I feel about it",
    },
  },
];

const NORMAL_ROWS = [
  {
    badge: 'yes',
    q: 'Is it normal to feel completely fine one week and terrible the next?',
    a: "Yes. Your cycle phases shift your hormones significantly — mood, energy, and body all follow. You're not unstable. You're cyclical.",
  },
  {
    badge: 'yes',
    q: 'Is rage or crying before my period normal?',
    a: 'Very. Luteal phase hormone drops affect your stress response at a neurological level. It\'s physiological, not personal.',
  },
  {
    badge: 'yes',
    q: 'Is it normal for my body to look different at different points in my cycle?',
    a: 'Yes. Bloating, skin changes, slight weight shifts — all real, cycle-driven, and temporary. Not something that\'s wrong with you.',
  },
  {
    badge: 'yes',
    q: 'Is it normal to feel exhausted before my period even if I\'ve slept enough?',
    a: "Yes. Progesterone disrupts deep sleep quality and acts as a mild sedative. You're not imagining it.",
  },
  {
    badge: 'common',
    q: 'Is it normal to feel really low or hopeless before my period?',
    a: "Common, yes. But if it's severe or affecting your daily life — that may be PMDD. Real and treatable. Worth more than just pushing through.",
  },
  {
    badge: 'check',
    q: 'Is period pain so bad I can\'t function normal?',
    a: "Mild cramping is normal. Pain that stops you doing things is not just part of it — it's a medical issue worth a doctor's visit. You don't have to endure it.",
  },
  {
    badge: 'yes',
    q: 'Is it normal to feel confused about whether something in a relationship was okay?',
    a: "Yes. Confusion is information. If something felt wrong, that matters — even if you can't articulate why yet.",
  },
  {
    badge: 'yes',
    q: 'Is it normal to feel lonely even when I\'m around people?',
    a: "Yes. Social loneliness and emotional loneliness are different things. The second kind doesn't go away just because you're surrounded by people.",
  },
  {
    badge: 'check',
    q: 'Is it normal to feel bad about how I look compared to people online?',
    a: "Common, especially pre-period. But if it's constant or affecting what you eat — that deserves more than a scroll limit.",
  },
];

const TOOLKIT_TILES = [
  {
    nova: false,
    bg: '#EEEDFE',
    border: 'rgba(127,119,221,0.35)',
    emoji: '🌬',
    name: '4-7-8 breath',
    nameColor: '#3C3489',
    descColor: '#534AB7',
    desc: "In 4 counts, hold 7, out 8. Activates your parasympathetic nervous system. Does something real when you're overwhelmed or in a rage spiral.",
  },
  {
    nova: false,
    bg: '#E1F5EE',
    border: 'rgba(13,148,136,0.25)',
    emoji: '🚶',
    name: 'Move for 10 minutes',
    nameColor: '#085041',
    descColor: '#0F6E56',
    desc: 'Not a workout. A walk, stretching, dancing alone in your room. Movement metabolises cortisol. Even 10 minutes genuinely shifts things.',
  },
  {
    nova: false,
    bg: '#FEF0EB',
    border: 'rgba(232,96,44,0.25)',
    emoji: '✍️',
    name: 'Finish this sentence',
    nameColor: '#AD3F1A',
    descColor: '#C94E22',
    desc: '"Right now I feel ___. What I actually need is ___." Writing the second blank changes what it feels like to need it.',
  },
  {
    nova: false,
    bg: '#FAECE7',
    border: 'rgba(216,90,48,0.25)',
    emoji: '🔥',
    name: 'Heat for cramps',
    nameColor: '#712B13',
    descColor: '#993C1D',
    desc: 'Heat pad or hot water bottle on your lower abdomen. Not a placebo — it genuinely relaxes the uterine muscle and reduces prostaglandin effects.',
  },
  {
    nova: false,
    bg: '#F9F6F1',
    border: '#E8E0D8',
    emoji: '📵',
    name: '20 minutes off the phone',
    nameColor: '#111111',
    descColor: '#555555',
    desc: 'Specifically during the luteal phase. Scrolling during a hard hormonal day is like adding fuel. 20 minutes off actually changes the feeling.',
  },
  {
    nova: true,
    bg: '#FBEAF0',
    border: 'rgba(212,83,126,0.25)',
    emoji: '💬',
    name: 'Talk to Nova',
    nameColor: '#72243E',
    descColor: '#993556',
    desc: "Not a helpline. A private space that knows your cycle, your chapter, and what's been going on — with no judgment and no record.",
  },
];

const BADGE_STYLES = {
  yes: { bg: '#E1F5EE', color: '#085041', label: 'Yes' },
  common: { bg: '#EEEDFE', color: '#534AB7', label: 'Common' },
  check: { bg: '#FEF0EB', color: '#AD3F1A', label: 'Worth checking' },
};

function FeatureCard({ f, onNovaFooter, cycleDay }) {
  const oraTipText = f.oraTip.replace(/__CYCLE_DAY__/g, String(cycleDay));
  return (
    <div
      style={{
        border: `0.5px solid ${FC.border}`,
        borderRadius: 18,
        overflow: 'hidden',
        background: '#FFFFFF',
        marginBottom: 16,
      }}
    >
      <div style={{ height: 4, background: f.accentColor }} />
      <div className="mychapter-feature-inner">
        <div className="mychapter-feature-img-wrap">
          <img src={f.imageSrc} alt={f.imageAlt} />
        </div>
        <div className="mychapter-feature-main">
          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', color: f.eyebrowColor, marginBottom: 8, fontFamily: FONT_SANS }}>
            {f.number} · {f.topic}
          </div>
          <h3 style={{ fontFamily: FONT_SERIF, fontSize: 15, fontWeight: 700, color: FC.ink, margin: '0 0 10px', lineHeight: 1.3 }}>{f.title}</h3>
          <div
            style={{
              background: f.quoteColors.bg,
              borderRadius: 10,
              padding: '10px 12px',
              marginBottom: 10,
            }}
          >
            <p style={{ fontSize: 12, fontStyle: 'italic', color: f.quoteColors.text, margin: 0, lineHeight: 1.55, fontFamily: FONT_SANS }}>{f.quote}</p>
          </div>
          <BodySegments parts={f.bodyParts} />
          <p style={{ fontSize: 12, color: FC.body, lineHeight: 1.65, margin: '12px 0 0', fontFamily: FONT_SANS }}>{f.body2}</p>
        </div>
        <div className="mychapter-feature-actions">
          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', color: FC.muted, marginBottom: 8, fontFamily: FONT_SANS }}>
            TRY THIS
          </div>
          {f.actions.map((a) => (
            <div key={a.name} style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: FC.ink, fontFamily: FONT_SANS, marginBottom: 3 }}>{a.name}</div>
              <div style={{ fontSize: 11, color: FC.body, lineHeight: 1.5, fontFamily: FONT_SANS }}>{a.desc}</div>
            </div>
          ))}
          <div
            style={{
              background: f.oraNoteColors.bg,
              borderRadius: 10,
              padding: '10px 12px',
              marginTop: 4,
            }}
          >
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', color: f.oraNoteColors.labelColor, marginBottom: 4, fontFamily: FONT_SANS }}>
              Nova note
            </div>
            <p style={{ fontSize: 11, color: f.oraNoteColors.textColor, margin: 0, lineHeight: 1.55, fontFamily: FONT_SANS }}>{oraTipText}</p>
          </div>
        </div>
      </div>
      <div
        style={{
          padding: '9px 20px',
          borderTop: `0.5px solid ${FC.border}`,
          background: '#FAFAFA',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        <span style={{ fontSize: 11, color: FC.muted, fontFamily: FONT_SANS }}>{f.footer.left}</span>
        <button
          type="button"
          onClick={() => onNovaFooter(f.footer.novaPrefill)}
          style={{
            fontSize: 11,
            fontWeight: 600,
            color: FC.accent,
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            fontFamily: FONT_SANS,
            padding: 0,
            textAlign: 'right',
          }}
        >
          {f.footer.right}
        </button>
      </div>
    </div>
  );
}

export default function MyChapter({ navigate, cycleDay = 14 }) {
  const nav = navigate || (() => {});
  const { setNovaDraft } = useApp();
  const narrow = useMediaQuery('(max-width: 720px)');

  const featuresRef = useRef(null);
  const normalsRef = useRef(null);
  const toolkitRef = useRef(null);

  const [expandedNormal, setExpandedNormal] = useState(null);
  const [askDraft, setAskDraft] = useState('');
  const [selectedFeatureIndex, setSelectedFeatureIndex] = useState(0);
  /** Which nav strip is "active" for underline (features vs normals vs toolkit). */
  const [navSection, setNavSection] = useState('features');

  const scrollTo = useCallback((ref) => {
    ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const openNova = useCallback(
    (text) => {
      if (text && setNovaDraft) setNovaDraft(text);
      nav('/nova');
    },
    [nav, setNovaDraft]
  );

  const jumpPills = [
    { label: 'Mood & rage', icon: '😤', ref: featuresRef, featureIndex: 0 },
    { label: 'Energy & sleep', icon: '😴', ref: featuresRef, featureIndex: 1 },
    { label: 'Body image', icon: '🪞', ref: featuresRef, featureIndex: 2 },
    { label: 'Am I normal?', icon: '❓', ref: normalsRef, section: 'normals' },
    { label: 'Relationships', icon: '💬', ref: featuresRef, featureIndex: 3 },
    { label: 'Hard day toolkit', icon: '🧰', ref: toolkitRef, section: 'toolkit' },
  ];

  return (
    <div
      style={{
        background: 'transparent',
        fontFamily: FONT_SANS,
        width: '100%',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
      }}
    >
      <style>{`
        .mychapter-feature-inner {
          display: grid;
          grid-template-columns: minmax(148px, 200px) minmax(0, 1fr) minmax(0, 1fr);
          gap: 18px;
          padding: 16px 20px;
          box-sizing: border-box;
          align-items: start;
        }
        .mychapter-feature-img-wrap {
          border-radius: 14px;
          overflow: hidden;
          background: #f0ebe3;
          align-self: start;
          position: relative;
          aspect-ratio: 3 / 4;
          max-height: 320px;
        }
        .mychapter-feature-img-wrap img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
        }
        @media (max-width: 900px) {
          .mychapter-feature-inner {
            grid-template-columns: 1fr;
          }
          .mychapter-feature-img-wrap {
            max-height: 200px;
            aspect-ratio: 16 / 9;
            max-width: 100%;
          }
        }
        .mychapter-toolkit-grid { grid-template-columns: repeat(3, 1fr) !important; }
        @media (max-width: 900px) {
          .mychapter-toolkit-grid { grid-template-columns: repeat(2, 1fr) !important; }
        }
        @media (max-width: 520px) {
          .mychapter-toolkit-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>

      {/* Section 1 — two modes */}
      <SecLabel>What do you need right now?</SecLabel>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: narrow ? '1fr' : '1fr 1fr',
          gap: 12,
        }}
      >
        <button
          type="button"
          onClick={() => {
            setNavSection('features');
            setSelectedFeatureIndex(0);
            scrollTo(featuresRef);
          }}
          style={{
            textAlign: 'left',
            cursor: 'pointer',
            background: '#EEEDFE',
            border: `0.5px solid ${FC.accentBorder}`,
            borderRadius: 14,
            padding: '14px 16px',
            fontFamily: 'inherit',
          }}
        >
          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', color: '#534AB7', marginBottom: 4 }}>I WANT TO UNDERSTAND</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#3C3489', marginBottom: 6 }}>Why is this happening to me?</div>
          <div style={{ fontSize: 11, color: '#534AB7', lineHeight: 1.5, marginBottom: 8 }}>
            Make sense of what my body and mood are doing — so it feels less random and less like something wrong with me
          </div>
          <div style={{ fontSize: 10, fontWeight: 600, color: FC.accent }}>→ Six honest explanations below</div>
        </button>
        <button
          type="button"
          onClick={() => scrollTo(toolkitRef)}
          style={{
            textAlign: 'left',
            cursor: 'pointer',
            background: '#E1F5EE',
            border: '0.5px solid rgba(13,148,136,0.25)',
            borderRadius: 14,
            padding: '14px 16px',
            fontFamily: 'inherit',
          }}
        >
          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', color: '#0F6E56', marginBottom: 4 }}>I NEED TO DO SOMETHING</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#085041', marginBottom: 6 }}>I&apos;m in it right now. Help.</div>
          <div style={{ fontSize: 11, color: '#0F6E56', lineHeight: 1.5, marginBottom: 8 }}>
            I don&apos;t want to read — I need something to do right now that will actually make today more bearable
          </div>
          <div style={{ fontSize: 10, fontWeight: 600, color: '#1D9E75' }}>→ Jump to hard day toolkit</div>
        </button>
      </div>

      {/* Section 2 — underline nav (matches product tabs) */}
      <div
        style={{
          ...tabNavContainer,
          borderBottom: `0.5px solid ${FC.border}`,
          overflowX: 'auto',
          flexWrap: 'nowrap',
          scrollbarWidth: 'none',
        }}
      >
        {jumpPills.map((p) => {
          const isFeature = p.featureIndex !== undefined;
          const active = isFeature
            ? navSection === 'features' && selectedFeatureIndex === p.featureIndex
            : navSection === p.section;
          return (
            <button
              key={p.label}
              type="button"
              onClick={() => {
                if (isFeature) {
                  setNavSection('features');
                  setSelectedFeatureIndex(p.featureIndex);
                } else {
                  setNavSection(p.section);
                }
                scrollTo(p.ref);
              }}
              style={{
                ...tabNavItemColored(active, FC.accent),
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                flexShrink: 0,
              }}
            >
              <span style={{ fontSize: 16, lineHeight: 1, flexShrink: 0 }} aria-hidden>
                {p.icon}
              </span>
              {p.label}
            </button>
          );
        })}
      </div>

      {/* Section 3 — features (one card at a time) */}
      <div ref={featuresRef} id="features">
        <SecLabel>What nobody explained</SecLabel>
        <FeatureCard f={FEATURES[selectedFeatureIndex]} cycleDay={cycleDay} onNovaFooter={openNova} />
      </div>

      {/* Section 4 — normals */}
      <div ref={normalsRef} id="normals">
        <SecLabel>Am I normal? Honest answers</SecLabel>
        <p style={{ fontSize: 12, color: FC.muted, marginBottom: 14, lineHeight: 1.6, marginTop: 0 }}>
          The question everyone is googling at midnight. Tap any row for the full answer. Yes / Common / Worth checking — no hedging, no disclaimers.
        </p>
        {NORMAL_ROWS.map((row, i) => {
          const bs = BADGE_STYLES[row.badge];
          const open = expandedNormal === i;
          return (
            <button
              key={i}
              type="button"
              onClick={() => setExpandedNormal(open ? null : i)}
              style={{
                width: '100%',
                textAlign: 'left',
                cursor: 'pointer',
                border: `0.5px solid ${FC.border}`,
                borderRadius: 8,
                padding: '10px 14px',
                marginBottom: 8,
                background: '#FFFFFF',
                fontFamily: 'inherit',
                boxSizing: 'border-box',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                <span
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    padding: '3px 8px',
                    borderRadius: 999,
                    background: bs.bg,
                    color: bs.color,
                    flexShrink: 0,
                    fontFamily: FONT_SANS,
                  }}
                >
                  {bs.label}
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: FC.ink, lineHeight: 1.4 }}>{row.q}</div>
                  {open && (
                    <div style={{ fontSize: 12, color: FC.body, lineHeight: 1.6, marginTop: 10, paddingTop: 10, borderTop: `0.5px solid ${FC.border}` }}>
                      {row.a}
                    </div>
                  )}
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Section 5 — toolkit */}
      <div ref={toolkitRef} id="toolkit">
        <SecLabel>Hard day toolkit — things that actually help</SecLabel>
        <p style={{ fontSize: 12, color: FC.muted, marginBottom: 14, lineHeight: 1.6, marginTop: 0 }}>
          Not &apos;have you tried yoga.&apos; Actual things — small, accessible, with a reason behind each one.
        </p>
        <div
          className="mychapter-toolkit-grid"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 10,
          }}
        >
          {TOOLKIT_TILES.map((t, i) => {
            const inner = (
              <>
                <div style={{ fontSize: 16, marginBottom: 6 }}>{t.emoji}</div>
                <div style={{ fontSize: 12, fontWeight: 700, color: t.nameColor, marginBottom: 4, fontFamily: FONT_SANS }}>{t.name}</div>
                <div style={{ fontSize: 11, color: t.descColor, lineHeight: 1.45, fontFamily: FONT_SANS }}>{t.desc}</div>
              </>
            );
            const shell = {
              borderRadius: 14,
              padding: '13px 14px',
              border: `0.5px solid ${t.border}`,
              background: t.bg,
              boxSizing: 'border-box',
              width: '100%',
            };
            if (t.nova) {
              return (
                <button
                  key={i}
                  type="button"
                  onClick={() => openNova(`I'm having a hard day — cycle day ${cycleDay}. Can we talk?`)}
                  style={{ ...shell, textAlign: 'left', cursor: 'pointer', fontFamily: 'inherit' }}
                >
                  {inner}
                </button>
              );
            }
            return (
              <div key={i} style={shell}>
                {inner}
              </div>
            );
          })}
        </div>
      </div>

      {/* Section 6 — Ask Nova */}
      <div
        style={{
          background: FC.accentSoft,
          border: `0.5px solid ${FC.accentBorder}`,
          borderRadius: 14,
          padding: '12px 14px',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          flexWrap: 'wrap',
          marginBottom: 8,
        }}
      >
        <NovaAvatarImg chapter="ttc" size={28} />
        <input
          value={askDraft}
          onChange={(e) => setAskDraft(e.target.value)}
          placeholder={`Ask Nova anything — she knows your cycle day ${cycleDay}, your chapter, and nothing here leaves this space...`}
          style={{
            flex: 1,
            minWidth: 160,
            background: '#FFFFFF',
            border: `0.5px solid ${FC.accentBorder}`,
            borderRadius: 999,
            padding: '8px 16px',
            fontSize: 13,
            fontFamily: FONT_SANS,
            color: FC.body,
            outline: 'none',
          }}
          onFocus={(e) => {
            e.target.style.borderColor = FC.accent;
          }}
          onBlur={(e) => {
            e.target.style.borderColor = FC.accentBorder;
          }}
        />
        <button
          type="button"
          onClick={() => {
            const q = askDraft.trim();
            openNova(q || `I'm on cycle day ${cycleDay} — can we talk?`);
            setAskDraft('');
          }}
          style={{
            background: FC.accent,
            color: '#FFFFFF',
            border: 'none',
            borderRadius: 999,
            padding: '8px 18px',
            fontSize: 13,
            fontWeight: 700,
            fontFamily: FONT_SANS,
            cursor: 'pointer',
            flexShrink: 0,
          }}
        >
          Ask →
        </button>
      </div>
    </div>
  );
}

